/**
 * ActivityPub / Fediverse API client.
 */

import { api } from './client';

export interface ActivityPubStatus {
  enabled: boolean;
  username: string;
  handle: string;
  actor_url: string;
  follower_count: number;
  comment_count: number;
  total_likes: number;
  total_boosts: number;
}

export interface ActivityPubFollower {
  id: string;
  actor_uri: string;
  actor_inbox: string;
  actor_shared_inbox: string | null;
  actor_name: string | null;
  actor_handle: string | null;
  accepted_at: string | null;
  created_at: string | null;
}

export interface ActivityPubComment {
  id: string;
  post_id: string;
  actor_uri: string;
  actor_name: string | null;
  actor_handle: string | null;
  content_html: string;
  in_reply_to: string | null;
  activity_uri: string;
  approved: boolean;
  created_at: string | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
}

export interface PaginationParams {
  page?: number;
  per_page?: number;
}

function buildQuery(params?: PaginationParams): string {
  if (!params) return '';
  const parts: string[] = [];
  if (params.page) parts.push(`page=${params.page}`);
  if (params.per_page) parts.push(`per_page=${params.per_page}`);
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export const activitypubApi = {
  getStatus: (siteSlug: string) =>
    api.get<ActivityPubStatus>(`/v1/sites/${siteSlug}/activitypub/status`),

  getFollowers: (siteSlug: string, params?: PaginationParams) =>
    api.get<PaginatedResponse<ActivityPubFollower>>(
      `/v1/sites/${siteSlug}/activitypub/followers${buildQuery(params)}`,
    ),

  getComments: (siteSlug: string, params?: PaginationParams) =>
    api.get<PaginatedResponse<ActivityPubComment>>(
      `/v1/sites/${siteSlug}/activitypub/comments${buildQuery(params)}`,
    ),

  approveComment: (siteSlug: string, id: string) =>
    api.post<{ status: string }>(`/v1/sites/${siteSlug}/activitypub/comments/${id}/approve`, {}),

  deleteComment: (siteSlug: string, id: string) =>
    api.delete<{ status: string }>(`/v1/sites/${siteSlug}/activitypub/comments/${id}`),

  enable: (siteSlug: string) =>
    api.post<{ status: string; username: string }>(
      `/v1/sites/${siteSlug}/activitypub/enable`,
      {},
    ),

  disable: (siteSlug: string) =>
    api.post<{ status: string }>(`/v1/sites/${siteSlug}/activitypub/disable`, {}),
};
